import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["additionalDomains", "onChangeMultiple", "popoverOpen", "popoverProps", "readOnly", "disabled", "setPopoverOpen", "showAutoSaveText", "onBlur", "onFocus", "inputRef"];
import { memo, useCallback, useMemo } from 'react';
import I18n from 'I18n';
import { parseMultiEnumValue, formatMultiEnumValue } from 'customer-data-property-utils/parseMultiEnumValue';
import DomainPopoverContent from './domain/DomainPopoverContent';
import useComponentMountMetric from '../metrics/useComponentMountMetric';
import { useProperty } from '../v2/hooks/useProperty';
import { usePropertyValue } from '../v2/hooks/usePropertyValue';
import emptyFunction from 'react-utils/emptyFunction';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { EMPTY_PROPERTY_VALUE } from '../constants/EmptyPropertyValue';
import { getTransferrableProps } from '../utils/getTransferrableProps';
import { ExpandingTextArea } from 'foundations-components/transitional/components/ExpandingTextArea';
import { H5 } from 'foundations-components/transitional/components/H5';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { jsx as _jsx } from "react/jsx-runtime";
const PropertyInputMultipleDomain = _ref => {
  let {
      additionalDomains,
      onChangeMultiple,
      popoverOpen,
      popoverProps,
      readOnly = false,
      disabled = false,
      setPopoverOpen,
      showAutoSaveText = false,
      onBlur = emptyFunction,
      onFocus = emptyFunction,
      inputRef
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  useComponentMountMetric('PropertyInputMultipleDomain');
  const property = useProperty();
  const value = usePropertyValue();
  const handleShowPopover = useCallback(() => {
    // We don't want to allow opening the popover if the input is disabled or
    // read only
    if (readOnly || disabled) {
      return;
    }
    setPopoverOpen(true);
  }, [disabled, readOnly, setPopoverOpen]);
  const domains = useMemo(() => {
    const result = [value || ''];
    if (!additionalDomains) {
      return result;
    }
    return [...result, ...parseMultiEnumValue(additionalDomains)];
  }, [value, additionalDomains]);
  const onDomainsChanged = newDomains => {
    const primaryDomainValue = newDomains[0];
    const secondaryDomainValue = formatMultiEnumValue(newDomains.slice(1));
    onChangeMultiple(Object.assign({}, primaryDomainValue !== value ? {
      [property.name]: primaryDomainValue
    } : undefined, secondaryDomainValue !== additionalDomains ? {
      hs_additional_domains: secondaryDomainValue
    } : undefined));
  };
  const handleChangeDomain = (index, domain) => {
    const updatedValue = [...domains];
    updatedValue[index] = domain;
    onDomainsChanged(updatedValue);
  };
  const handleAddDomain = newDomain => {
    const updatedValue = [...domains, newDomain];
    onDomainsChanged(updatedValue);
  };
  const handleRemoveDomain = domainToRemove => {
    const updatedValue = domains.filter(domain => domain !== domainToRemove);
    onDomainsChanged(updatedValue);
  };
  const handlePromoteDomain = domainToPromote => {
    const valueWithoutPromotedDomain = domains.filter(domain => domain !== domainToPromote);
    const updatedValue = [domainToPromote, ...valueWithoutPromotedDomain];
    onDomainsChanged(updatedValue);
  };
  const popoverContent = /*#__PURE__*/_jsx(DomainPopoverContent, {
    domains: domains,
    onChangeDomain: handleChangeDomain,
    onRemoveDomain: handleRemoveDomain,
    onPromoteDomain: handlePromoteDomain,
    onAddDomain: handleAddDomain,
    showAutoSaveText: showAutoSaveText,
    onExistingDomainInputBlur: onBlur,
    onExistingDomainInputFocus: onFocus
  });
  const isDomainsListEmpty = domains.length === 1 && domains[0] === '';
  const displayValue = isDomainsListEmpty ? EMPTY_PROPERTY_VALUE : I18n.formatList(domains);
  return /*#__PURE__*/_jsx(Popover, Object.assign({
    inlineSize: "350px",
    placement: "right"
  }, popoverProps, {
    open: popoverOpen,
    onOpenChange: evt => setPopoverOpen(evt.target.value),
    content: {
      header: /*#__PURE__*/_jsx(H5, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputMultipleDomain.label"
        })
      }),
      body: popoverContent
    },
    showCloseButton: true,
    children: /*#__PURE__*/_jsx(ExpandingTextArea, Object.assign({}, getTransferrableProps(props), {
      "data-test-id": "domain-input",
      disabled: disabled,
      inputRef: inputRef,
      onChange: emptyFunction,
      onFocus: handleShowPopover,
      readOnly: readOnly,
      shrink: true,
      value: displayValue
    }))
  }));
};
export default /*#__PURE__*/memo(PropertyInputMultipleDomain);